import { useMemo } from "react";
import type { Row } from "../types.ts";
import { formatAmount, isoWeek } from "../format.ts";
import { CurrencyInput } from "./CurrencyInput.tsx";

type Props = {
  rows: Row[];
  onPatch: (id: string, patch: Partial<Row>) => void;
  onDelete: (id: string) => void;
  onUnsnooze: (id: string) => void;
};

function sortByDueDate(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

export function SnoozedTable({ rows, onPatch, onDelete, onUnsnooze }: Props) {
  const sorted = useMemo(() => sortByDueDate(rows), [rows]);
  const sum = useMemo(
    () => rows.reduce((a, r) => a + (Number(r.amount) || 0), 0),
    [rows],
  );

  return (
    <section className="snoozed" aria-labelledby="snoozed-title">
      <div className="snoozed-head">
        <h2 id="snoozed-title">Snoozade</h2>
        <span className="snoozed-meta">
          {rows.length} {rows.length === 1 ? "rad" : "rader"} utanför kalkylen ·{" "}
          <b>{formatAmount(sum)}</b>
        </span>
      </div>
      <table className="snoozed-table">
        <thead>
          <tr>
            <th className="col-week">Vecka</th>
            <th className="col-due">Förfallo</th>
            <th className="col-supplier">Leverantör</th>
            <th className="col-amount num">Belopp</th>
            <th className="col-restore"></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id} className="row row-snoozed" data-row-id={row.id}>
              <td className="col-week num">{row.week}</td>
              <td className="col-due">
                <input
                  type="date"
                  required
                  value={row.dueDate}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    onPatch(row.id, { dueDate: v, week: isoWeek(v) });
                  }}
                />
              </td>
              <td className="col-supplier">
                <input
                  type="text"
                  value={row.supplier}
                  onChange={(e) =>
                    onPatch(row.id, { supplier: e.currentTarget.value })
                  }
                />
              </td>
              <td
                className={
                  "col-amount num amount" +
                  (row.amount < 0 ? " amount-neg" : "")
                }
              >
                <CurrencyInput
                  value={row.amount}
                  onChange={(n) => onPatch(row.id, { amount: n })}
                />
              </td>
              <td className="col-restore">
                <button
                  className="restore-btn"
                  title="Återta till kalkylen"
                  onClick={() => onUnsnooze(row.id)}
                >
                  Återta
                </button>
                <button
                  className="delete-btn"
                  tabIndex={-1}
                  title="Ta bort rad"
                  onClick={() => onDelete(row.id)}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
